import moment from 'moment';

/**
 * Servicio para lógica de negocio relacionada con citas
 */
class CitasLogicService {

  /**
   * Calcula el tiempo total de servicios seleccionados
   * @param {Array} serviciosSeleccionados - Array de servicios
   * @param {Array} serviciosCompletos - Array de servicios completos para referencias
   * @returns {number} Tiempo total en minutos
   */
  calcularTiempoTotal(serviciosSeleccionados, serviciosCompletos = []) {
    try {
      // Validar entrada
      if (!serviciosSeleccionados || !Array.isArray(serviciosSeleccionados) || serviciosSeleccionados.length === 0) {
        return 0;
      }
      
      // Inicializar el tiempo total
      let tiempoTotalMinutos = 0;
      
      // Procesar cada servicio según su tipo
      serviciosSeleccionados.forEach((servicio, index) => {
        let tiempo = 0;
        
        if (typeof servicio === 'string') {
          // Es un ID de servicio, buscar en la lista de servicios
          const servicioEncontrado = serviciosCompletos.find(s => s.id === servicio);
          if (servicioEncontrado) {
            tiempo = servicioEncontrado.TiempoEstimado || 30;
          } else {
            tiempo = 30; // Valor por defecto
          }
        } else if (servicio && typeof servicio === 'object') {
          // Es un objeto de servicio
          if (servicio.Tiempo !== undefined) {
            // Si tiene un campo Tiempo, usarlo directamente
            tiempo = servicio.Tiempo;
          } else if (servicio.TiempoEstimado !== undefined) {
            // Si tiene un campo TiempoEstimado, usarlo
            tiempo = servicio.TiempoEstimado;
          } else if (servicio.id || servicio.Servicio) {
            // Si tiene ID pero no tiempo, buscar el servicio en la lista
            const servicioId = servicio.id || (typeof servicio.Servicio === 'string' ? servicio.Servicio : 
                                              (Array.isArray(servicio.Servicio) && servicio.Servicio.length > 0 ? servicio.Servicio[0] : null));
            if (servicioId) {
              const servicioEncontrado = serviciosCompletos.find(s => s.id === servicioId);
              if (servicioEncontrado) {
                tiempo = servicioEncontrado.TiempoEstimado || 30;
              } else {
                tiempo = 30; // Valor por defecto
              }
            } else {
              tiempo = 30; // Valor por defecto
            }
          } else {
            tiempo = 30; // Valor por defecto
          }
        } else {
          tiempo = 30; // Valor por defecto
        }
        
        // Sumar al tiempo total
        tiempoTotalMinutos += tiempo;
      });
      
      return tiempoTotalMinutos;
    } catch (error) {
      console.error('Error al calcular tiempo total:', error);
      return 60; // Valor por defecto en caso de error
    }
  }

  /**
   * Calcula la hora de fin basada en hora de inicio y duración
   * @param {moment.Moment|string} horaInicio - Hora de inicio
   * @param {moment.Moment} fechaSeleccionada - Fecha seleccionada 
   * @param {number} duracionMinutos - Duración en minutos
   * @param {Object} horarioAtencionParsed - Horario de atención parseado de la sucursal
   * @returns {Object} { horaFin: moment, ajustada: boolean }
   */
  calcularHoraFin(horaInicio, fechaSeleccionada, duracionMinutos, horarioAtencionParsed = null) {
    if (!horaInicio || !fechaSeleccionada) {
      return { horaFin: null, ajustada: false };
    }
    
    let inicio;
    if (typeof horaInicio === 'string') {
      // Si es una cadena de texto (formato HH:mm), combinarla con la fecha seleccionada
      inicio = moment(fechaSeleccionada).set({
        hour: parseInt(horaInicio.split(':')[0], 10),
        minute: parseInt(horaInicio.split(':')[1], 10),
        second: 0
      });
    } else {
      // Si ya es un objeto moment, asegurarse de que tenga la fecha correcta
      inicio = moment(fechaSeleccionada).set({
        hour: horaInicio.hour(),
        minute: horaInicio.minute(),
        second: 0
      });
    }
    
    if (!inicio.isValid()) {
      return { horaFin: null, ajustada: false };
    }
    
    // Crear un nuevo momento para no modificar el original y añadir los minutos
    let fin = inicio.clone().add(duracionMinutos, 'minutes');
    
    // Si hay horario de atención parseado, verificar si la hora de fin está dentro del horario
    if (horarioAtencionParsed) {
      const resultado = this._ajustarHoraFinSegunHorarioSucursal(inicio, duracionMinutos, horarioAtencionParsed, fechaSeleccionada);
      return { horaFin: resultado.horaFin, ajustada: resultado.ajustada };
    }
    
    return { horaFin: fin, ajustada: false };
  }

  /**
   * Cuenta las citas simultáneas en un slot de tiempo específico
   * @param {moment.Moment} fecha - Fecha del slot
   * @param {moment.Moment} hora - Hora del slot
   * @param {Array} citas - Array de citas existentes
   * @param {number} duracionMinutos - Duración estimada de la nueva cita
   * @returns {number} Cantidad de citas que se solapan con el horario
   */
  contarCitasSimultaneas(fecha, hora, citas = [], duracionMinutos = 30) {
    const dateTime = moment(fecha.format('YYYY-MM-DD') + ' ' + hora.format('HH:mm'), 'YYYY-MM-DD HH:mm');
    const endTime = moment(dateTime).add(duracionMinutos, 'minutes');
    
    return citas.filter(cita => {
      if (!cita.Fecha || !cita.Hora || cita.Estado === 'Cancelada') return false;
      
      const citaDateTime = moment(cita.Fecha + ' ' + cita.Hora, 'YYYY-MM-DD HH:mm');
      const citaEndTime = moment(citaDateTime).add(cita.TiempoEstimado || 30, 'minutes');
      
      // Verificar si el horario solicitado se solapa con esta cita
      return dateTime < citaEndTime && endTime > citaDateTime;
    }).length;
  }

  /**
   * Verifica si un slot de tiempo está disponible considerando capacidad de clientes
   * @param {moment.Moment} fecha - Fecha del slot
   * @param {moment.Moment} hora - Hora del slot
   * @param {Array} citas - Array de citas existentes
   * @param {number} capacidadClientes - Capacidad máxima de clientes simultáneos (default: 1 para compatibilidad)
   * @param {number} duracionMinutos - Duración estimada de la nueva cita
   * @returns {boolean} true si está disponible
   */
  isTimeSlotAvailable(fecha, hora, citas = [], capacidadClientes = 1, duracionMinutos = 30) {
    // Combinar fecha y hora
    const dateTime = moment(fecha.format('YYYY-MM-DD') + ' ' + hora.format('HH:mm'), 'YYYY-MM-DD HH:mm');
    
    // No permitir horas pasadas para el día actual
    if (fecha.isSame(moment(), 'day') && hora.isBefore(moment())) {
      return false;
    }
    
    // Contar citas simultáneas en este horario
    const citasSimultaneas = this.contarCitasSimultaneas(fecha, hora, citas, duracionMinutos);
    
    // Verificar si hay capacidad disponible
    return citasSimultaneas < capacidadClientes;
  }

  /**
   * Verifica si hay citas en un día específico
   * @param {moment.Moment} dia - Día a verificar
   * @param {Array} citas - Array de citas
   * @param {boolean} isAdmin - Si es administrador
   * @param {Object} clienteData - Datos del cliente (si no es admin)
   * @returns {Object|boolean} Información sobre las citas del día o false si no hay
   */
  hasCitasOnDay(dia, citas = [], isAdmin = true, clienteData = null) {
    // Si no hay citas, retornar false inmediatamente
    if (!citas || citas.length === 0) return false;
    
    // Formatear el día para comparación
    const dayFormatted = dia.format('YYYY-MM-DD');
    
    // Filtrar citas del día
    const citasDelDia = citas.filter(cita => {
      // Verificar que la cita tenga fecha
      if (!cita.Fecha) return false;
      
      // Comparar la fecha directamente
      const sameDay = cita.Fecha === dayFormatted;
      const notCancelled = cita.Estado !== 'Cancelada';
      
      // Para clientes, solo mostrar indicador para sus propias citas
      if (!isAdmin && clienteData) {
        // Si el cliente tiene datos, verificar que la cita sea suya
        if (clienteData.id) {
          // Verificar si la cita tiene datos de cliente
          const isClientCita = cita.clienteData && cita.clienteData.id === clienteData.id;
          return sameDay && notCancelled && isClientCita;
        }
        return false;
      }
      
      // Para administradores, mostrar todas las citas
      return sameDay && notCancelled;
    });
    
    if (citasDelDia.length === 0) return false;
    
    // Para clientes, solo retornar true/false (sin diferenciación de colores)
    if (!isAdmin) return true;
    
    // Para admin/superadmin, analizar tipos de citas
    const tieneCompletadas = citasDelDia.some(cita => 
      cita.Estado === 'Completada' || cita.finalizada === true
    );
    
    const tienePendientes = citasDelDia.some(cita => 
      cita.Estado !== 'Completada' && cita.finalizada !== true && 
      cita.Estado !== 'No Asistió' && cita.Estado !== 'Cancelada'
    );
    
    return {
      hasAppointments: true,
      hasCompleted: tieneCompletadas,
      hasPending: tienePendientes,
      total: citasDelDia.length
    };
  }

  /**
   * Obtiene todas las citas de un día específico
   * @param {moment.Moment} dia - Día seleccionado
   * @param {Array} citas - Array de citas
   * @param {boolean} isAdmin - Si es administrador
   * @param {Object} clienteData - Datos del cliente (si no es admin)
   * @param {Array} servicios - Array de servicios para enriquecer datos
   * @returns {Array} Citas del día procesadas
   */
  getCitasOnDay(dia, citas = [], isAdmin = true, clienteData = null, servicios = []) {
    // Si no hay citas o día seleccionado, retornar array vacío
    if (!citas || citas.length === 0 || !dia) return [];
    
    // Formatear el día para comparación
    const dayFormatted = dia.format('YYYY-MM-DD');
    
    const citasDelDia = citas.filter(cita => {
      // Verificar que la cita tenga fecha
      if (!cita.Fecha) return false;
      
      // Comparar la fecha directamente
      const sameDay = cita.Fecha === dayFormatted;
      
      // Para clientes, solo mostrar sus propias citas
      if (!isAdmin && clienteData) {
        const isClientCita = cita.clienteData && cita.clienteData.id === clienteData.id;
        return sameDay && isClientCita;
      }
      
      // Para administradores, mostrar todas las citas
      return sameDay;
    }).sort((a, b) => {
      // Ordenar por hora
      if (!a.Hora) return 1;
      if (!b.Hora) return -1;
      return moment(a.Hora, 'HH:mm').diff(moment(b.Hora, 'HH:mm'));
    });
    
    // Procesar las citas para enriquecer con datos de servicios
    const citasProcesadas = citasDelDia.map(cita => {
      return this._enriquecerCitaConServicios(cita, servicios);
    });
    
    return citasProcesadas;
  }

  /**
   * Formatea servicios seleccionados para el envío
   * @param {Array} serviciosSeleccionados - Servicios seleccionados
   * @returns {Array} Servicios formateados
   */
  formatearServiciosParaEnvio(serviciosSeleccionados) {
    return serviciosSeleccionados.map((servicio) => {
      // Verificar si el servicio ya tiene el formato correcto
      if (typeof servicio === 'string') {
        // Servicio ya es un ID
        return servicio;
      } else if (servicio && typeof servicio === 'object') {
        // Si el servicio tiene un ID, usarlo directamente
        if (servicio.id) {
          return {
            id: servicio.id,
            Servicio: servicio.id,
            Tiempo: servicio.Tiempo || 0,
            Precio: servicio.Precio || 0
          };
        } else if (servicio.ID) {
          return {
            id: servicio.ID,
            Servicio: servicio.ID,
            Tiempo: servicio.Tiempo || 0,
            Precio: servicio.Precio || 0
          };
        } else if (servicio.Servicio) {
          // Si tiene un campo Servicio, usarlo
          const servicioId = Array.isArray(servicio.Servicio) ? 
            servicio.Servicio[0] : servicio.Servicio;
          
          return {
            Servicio: servicioId,
            Tiempo: servicio.Tiempo || 0,
            Precio: servicio.Precio || 0
          };
        } else {
          // Intentar extraer algún identificador
          const keys = Object.keys(servicio);
          if (keys.length > 0) {
            return {
              Servicio: servicio[keys[0]],
              Tiempo: servicio.Tiempo || 0,
              Precio: servicio.Precio || 0
            };
          }
          return null; // Servicio inválido
        }
      } else {
        return null; // Servicio inválido
      }
    }).filter(servicio => servicio !== null); // Filtrar servicios inválidos
  }

  /**
   * Ajusta la hora de fin según el horario de atención de la sucursal
   * Puede extender el trabajo a múltiples días si es necesario
   * @private
   */
  _ajustarHoraFinSegunHorarioSucursal(horaInicio, duracionMinutos, horarioSucursal, fechaSeleccionada) {
    return this._calcularHoraFinConMultiplesDias(horaInicio, duracionMinutos, horarioSucursal, fechaSeleccionada, 0);
  }

  /**
   * Calcula la hora de fin considerando múltiples días laborales
   * @private
   */
  _calcularHoraFinConMultiplesDias(horaInicio, minutosRestantes, horarioSucursal, fechaActual, diasProcesados) {
    // Limitar la recursividad para evitar bucles infinitos (máximo 14 días)
    if (diasProcesados > 14 || minutosRestantes <= 0) {
      return { horaFin: horaInicio, ajustada: diasProcesados > 0 };
    }

    const diaSemana = fechaActual.locale('es').format('dddd').toLowerCase();
    const diaNormalizado = diaSemana
      .replace('á', 'a')
      .replace('é', 'e')
      .replace('í', 'i')
      .replace('ó', 'o')
      .replace('ú', 'u');
    
    // Obtener horario del día específico
    const horarioDia = horarioSucursal[diaNormalizado];
    
    // Si el día no tiene horario activo, buscar el siguiente día laboral
    if (!horarioDia || !horarioDia.activo) {
      const siguienteDiaLaboral = this._buscarSiguienteDiaLaboral(fechaActual, horarioSucursal);
      if (siguienteDiaLaboral) {
        const nuevaFechaInicio = this._obtenerInicioDelDia(siguienteDiaLaboral, horarioSucursal);
        return this._calcularHoraFinConMultiplesDias(
          nuevaFechaInicio, 
          minutosRestantes, 
          horarioSucursal, 
          siguienteDiaLaboral, 
          diasProcesados + 1
        );
      }
      return { horaFin: horaInicio, ajustada: diasProcesados > 0 };
    }

    let minutosDisponiblesHoy = 0;
    let horaFinDelDia = null;
    let siguienteHoraInicio = horaInicio.clone();

    // Calcular minutos disponibles en el turno de mañana
    if (horarioDia.mañana && horarioDia.mañana.activo && horarioDia.mañana.inicio && horarioDia.mañana.fin) {
      const inicioMañana = moment(fechaActual).set({
        hour: parseInt(horarioDia.mañana.inicio.split(':')[0]),
        minute: parseInt(horarioDia.mañana.inicio.split(':')[1]),
        second: 0
      });
      const finMañana = moment(fechaActual).set({
        hour: parseInt(horarioDia.mañana.fin.split(':')[0]),
        minute: parseInt(horarioDia.mañana.fin.split(':')[1]),
        second: 0
      });

      // Si estamos en o antes del turno de mañana
      if (siguienteHoraInicio.isSameOrBefore(finMañana)) {
        // Ajustar hora de inicio si es antes del turno de mañana
        if (siguienteHoraInicio.isBefore(inicioMañana)) {
          siguienteHoraInicio = inicioMañana.clone();
        }
        
        // Calcular minutos disponibles en la mañana
        if (siguienteHoraInicio.isBefore(finMañana)) {
          const minutosEnMañana = finMañana.diff(siguienteHoraInicio, 'minutes');
          minutosDisponiblesHoy += minutosEnMañana;
          horaFinDelDia = finMañana.clone();
          siguienteHoraInicio = finMañana.clone();
        }
      }
    }

    // Calcular minutos disponibles en el turno de tarde
    if (horarioDia.tarde && horarioDia.tarde.activo && horarioDia.tarde.inicio && horarioDia.tarde.fin) {
      const inicioTarde = moment(fechaActual).set({
        hour: parseInt(horarioDia.tarde.inicio.split(':')[0]),
        minute: parseInt(horarioDia.tarde.inicio.split(':')[1]),
        second: 0
      });
      const finTarde = moment(fechaActual).set({
        hour: parseInt(horarioDia.tarde.fin.split(':')[0]),
        minute: parseInt(horarioDia.tarde.fin.split(':')[1]),
        second: 0
      });

      // Si aún hay tiempo por procesar y estamos en o antes del turno de tarde
      if (siguienteHoraInicio.isSameOrBefore(finTarde)) {
        // Ajustar hora de inicio si es antes del turno de tarde
        if (siguienteHoraInicio.isBefore(inicioTarde)) {
          siguienteHoraInicio = inicioTarde.clone();
        }
        
        // Calcular minutos disponibles en la tarde
        if (siguienteHoraInicio.isBefore(finTarde)) {
          const minutosEnTarde = finTarde.diff(siguienteHoraInicio, 'minutes');
          minutosDisponiblesHoy += minutosEnTarde;
          horaFinDelDia = finTarde.clone();
        }
      }
    }

    // Si el trabajo cabe en el día actual
    if (minutosRestantes <= minutosDisponiblesHoy) {
      // Distribuir los minutos restantes a través de los turnos del día
      return this._distribuirMinutosEnDia(
        horaInicio, 
        minutosRestantes, 
        horarioDia, 
        fechaActual, 
        diasProcesados > 0
      );
    }

    // Si el trabajo no cabe en el día actual, continuar al siguiente día laboral
    const minutosRestantesDespuesDeHoy = minutosRestantes - minutosDisponiblesHoy;
    const siguienteDiaLaboral = this._buscarSiguienteDiaLaboral(fechaActual, horarioSucursal);
    
    if (siguienteDiaLaboral) {
      const nuevaFechaInicio = this._obtenerInicioDelDia(siguienteDiaLaboral, horarioSucursal);
      return this._calcularHoraFinConMultiplesDias(
        nuevaFechaInicio,
        minutosRestantesDespuesDeHoy,
        horarioSucursal,
        siguienteDiaLaboral,
        diasProcesados + 1
      );
    }

    // Si no hay más días laborales disponibles, terminar al final del día actual
    return { 
      horaFin: horaFinDelDia || horaInicio, 
      ajustada: true 
    };
  }

  /**
   * Distribuye los minutos restantes en los turnos del día actual
   * @private
   */
  _distribuirMinutosEnDia(horaInicio, minutosRestantes, horarioDia, fechaActual, yaAjustada) {
    let minutosADistribuir = minutosRestantes;
    let horaActual = horaInicio.clone();

    // Procesar turno de mañana
    if (horarioDia.mañana && horarioDia.mañana.activo && horarioDia.mañana.inicio && horarioDia.mañana.fin) {
      const inicioMañana = moment(fechaActual).set({
        hour: parseInt(horarioDia.mañana.inicio.split(':')[0]),
        minute: parseInt(horarioDia.mañana.inicio.split(':')[1]),
        second: 0
      });
      const finMañana = moment(fechaActual).set({
        hour: parseInt(horarioDia.mañana.fin.split(':')[0]),
        minute: parseInt(horarioDia.mañana.fin.split(':')[1]),
        second: 0
      });

      // Si comenzamos en el turno de mañana
      if (horaActual.isSameOrAfter(inicioMañana) && horaActual.isBefore(finMañana)) {
        const minutosDisponiblesMañana = finMañana.diff(horaActual, 'minutes');
        
        if (minutosADistribuir <= minutosDisponiblesMañana) {
          // Termina en el turno de mañana
          return { 
            horaFin: horaActual.clone().add(minutosADistribuir, 'minutes'), 
            ajustada: yaAjustada 
          };
        } else {
          // Continúa al turno de tarde
          minutosADistribuir -= minutosDisponiblesMañana;
          horaActual = finMañana.clone();
        }
      }
    }

    // Procesar turno de tarde
    if (horarioDia.tarde && horarioDia.tarde.activo && horarioDia.tarde.inicio && horarioDia.tarde.fin) {
      const inicioTarde = moment(fechaActual).set({
        hour: parseInt(horarioDia.tarde.inicio.split(':')[0]),
        minute: parseInt(horarioDia.tarde.inicio.split(':')[1]),
        second: 0
      });
      const finTarde = moment(fechaActual).set({
        hour: parseInt(horarioDia.tarde.fin.split(':')[0]),
        minute: parseInt(horarioDia.tarde.fin.split(':')[1]),
        second: 0
      });

      // Si comenzamos en el turno de tarde o venimos de la mañana
      if (horaActual.isSameOrBefore(inicioTarde)) {
        horaActual = inicioTarde.clone();
      }

      if (horaActual.isBefore(finTarde)) {
        const minutosDisponiblesTarde = finTarde.diff(horaActual, 'minutes');
        
        if (minutosADistribuir <= minutosDisponiblesTarde) {
          // Termina en el turno de tarde
          return { 
            horaFin: horaActual.clone().add(minutosADistribuir, 'minutes'), 
            ajustada: true 
          };
        } else {
          // Se extiende más allá del turno de tarde
          return { 
            horaFin: finTarde.clone(), 
            ajustada: true 
          };
        }
      }
    }

    // Si llegamos aquí, no pudo distribuirse correctamente
    return { horaFin: horaActual, ajustada: true };
  }

  /**
   * Busca el siguiente día laboral disponible
   * @private
   */
  _buscarSiguienteDiaLaboral(fechaActual, horarioSucursal) {
    let fechaCandidato = fechaActual.clone().add(1, 'day');
    let diasBuscados = 0;

    while (diasBuscados < 14) { // Buscar hasta 14 días
      const diaSemana = fechaCandidato.locale('es').format('dddd').toLowerCase();
      const diaNormalizado = diaSemana
        .replace('á', 'a')
        .replace('é', 'e')
        .replace('í', 'i')
        .replace('ó', 'o')
        .replace('ú', 'u');
      
      const horarioDia = horarioSucursal[diaNormalizado];
      
      // Verificar si el día tiene horario activo
      if (horarioDia && horarioDia.activo && 
          ((horarioDia.mañana && horarioDia.mañana.activo) || 
           (horarioDia.tarde && horarioDia.tarde.activo))) {
        return fechaCandidato;
      }

      fechaCandidato.add(1, 'day');
      diasBuscados++;
    }

    return null; // No se encontró día laboral
  }

  /**
   * Obtiene la hora de inicio del día laboral
   * @private
   */
  _obtenerInicioDelDia(fecha, horarioSucursal) {
    const diaSemana = fecha.locale('es').format('dddd').toLowerCase();
    const diaNormalizado = diaSemana
      .replace('á', 'a')
      .replace('é', 'e')
      .replace('í', 'i')
      .replace('ó', 'o')
      .replace('ú', 'u');
    
    const horarioDia = horarioSucursal[diaNormalizado];
    
    if (horarioDia && horarioDia.activo) {
      // Priorizar turno de mañana si está activo
      if (horarioDia.mañana && horarioDia.mañana.activo && horarioDia.mañana.inicio) {
        return moment(fecha).set({
          hour: parseInt(horarioDia.mañana.inicio.split(':')[0]),
          minute: parseInt(horarioDia.mañana.inicio.split(':')[1]),
          second: 0
        });
      }
      // Si no hay mañana, usar turno de tarde
      else if (horarioDia.tarde && horarioDia.tarde.activo && horarioDia.tarde.inicio) {
        return moment(fecha).set({
          hour: parseInt(horarioDia.tarde.inicio.split(':')[0]),
          minute: parseInt(horarioDia.tarde.inicio.split(':')[1]),
          second: 0
        });
      }
    }

    // Fallback: inicio del día a las 9:00
    return moment(fecha).set({ hour: 9, minute: 0, second: 0 });
  }


  /**
   * Enriquece una cita con datos de servicios
   * @private
   */
  _enriquecerCitaConServicios(cita, servicios) {
    // Si la cita ya tiene servicios seleccionados, usarlos
    if (cita.ServiciosSeleccionados && Array.isArray(cita.ServiciosSeleccionados) && cita.ServiciosSeleccionados.length > 0) {
      // Asegurarse de que cada servicio tenga precio
      const serviciosConPrecio = cita.ServiciosSeleccionados.map(servicio => {
        // Si el servicio ya tiene precio, usarlo
        if (servicio.Precio) {
          return servicio;
        }
        
        // Buscar el servicio en la lista completa de servicios
        const servicioCompleto = servicios.find(s => {
          return s.id === (servicio.Servicio ? 
                           (Array.isArray(servicio.Servicio) ? servicio.Servicio[0] : servicio.Servicio) 
                           : servicio.id);
        });
        
        // Si se encontró el servicio completo, usar su precio
        if (servicioCompleto) {
          return {
            ...servicio,
            Precio: servicioCompleto.Precio || 0,
            Nombre: servicioCompleto.Nombre,
            ServicioData: servicioCompleto
          };
        }
        
        return servicio;
      });
      
      return {
        ...cita,
        ServiciosSeleccionados: serviciosConPrecio,
        Servicios: serviciosConPrecio
      };
    }
    
    return cita;
  }
}

// Exportar instancia singleton
const citasLogicService = new CitasLogicService();
export default citasLogicService;